import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EnumBadge } from "@/components/badges";
import { formatDuration } from "@/lib/formatters";
import { cn } from "@/utils";

interface StructuredOutputViewProps {
  outputs: Record<string, unknown>;
}

const KNOWN_FIELDS = [
  { key: "pa_status", label: "PA Status" },
  { key: "pa_details", label: "PA Details" },
  { key: "fail_reason", label: "Fail Reason" },
  { key: "fail_details", label: "Fail Details" },
  { key: "reference_number", label: "Reference #" },
  { key: "call_duration", label: "Duration" },
  { key: "success", label: "Success" },
  { key: "alternate_contact_number", label: "Alt Contact #" },
];

export function StructuredOutputView({ outputs }: StructuredOutputViewProps) {
  const [open, setOpen] = useState(false);

  if (!outputs || Object.keys(outputs).length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No structured output
      </p>
    );
  }

  const knownEntries = KNOWN_FIELDS.filter(
    (f) => outputs[f.key] !== undefined && outputs[f.key] !== null
  );

  return (
    <div className="space-y-3">
      {/* Known fields as key-value pairs */}
      <div className="space-y-2">
        {knownEntries.map(({ key, label }) => {
          const value = outputs[key];
          return (
            <div key={key} className="flex items-start gap-2 text-sm">
              <span className="min-w-[100px] text-muted-foreground font-medium">
                {label}:
              </span>
              <span>
                {key === "pa_status" || key === "fail_reason" ? (
                  <EnumBadge value={String(value)} />
                ) : key === "success" ? (
                  <Badge
                    className={cn(
                      value === true || value === "true"
                        ? "bg-green-100 text-green-800 hover:bg-green-100"
                        : "bg-red-100 text-red-800 hover:bg-red-100"
                    )}
                  >
                    {String(value)}
                  </Badge>
                ) : key === "call_duration" ? (
                  formatDuration(Number(value))
                ) : (
                  String(value)
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Raw JSON collapsible */}
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 text-xs">
            <ChevronDown
              className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
            />
            {open ? "Hide" : "Show"} raw JSON
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="mt-2 max-h-60 overflow-auto rounded-md bg-muted p-3 text-xs">
            {JSON.stringify(outputs, null, 2)}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
